import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { EmailContact } from '../../../../types/email';
import Button from '../../../ui/Button';

interface AddContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Omit<EmailContact, 'isGlobal' | 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  contact?: EmailContact | null;
}

const validationSchema = Yup.object({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  company: Yup.string().required('Company is required'),
  role: Yup.string(),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required')
    .test('not-fake-email', 'Please enter a valid email address', (value) => {
      if (!value) return true;
      const fakeEmailPatterns = [
        /test@test\.com/i,
        /example@example\.com/i,
        /test@example\.com/i,
        /example@test\.com/i,
      ];
      return !fakeEmailPatterns.some((pattern) => pattern.test(value));
    }),
});

const AddContactModal: React.FC<AddContactModalProps> = ({ isOpen, onClose, onSave, contact }) => {
  const formik = useFormik({
    initialValues: {
      firstName: contact?.firstName || '',
      lastName: contact?.lastName || '',
      company: contact?.company || '',
      role: contact?.role || '',
      email: contact?.email || '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await onSave(values);
        formik.resetForm();
      } catch (error) {
        console.error('Failed to save contact:', error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-black">
            {contact ? 'Edit Contact' : 'Add Contact'}
          </h3>
          <button
            onClick={onClose}
            className="p-1 transition-colors rounded-md text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700 border border-transparent"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={formik.handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block mb-1 text-sm font-medium text-neutral-700">
              First Name <span className="text-danger-500">*</span>
            </label>
            <input
              type="text"
              name="firstName"
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full p-2 text-sm border border-neutral-300 rounded-md focus:border-primary-500"
            />
            {formik.touched.firstName && formik.errors.firstName && (
              <p className="mt-1 text-xs text-danger-500">{formik.errors.firstName}</p>
            )}
          </div>

          <div>
            <label className="block mb-1 text-sm font-medium text-neutral-700">
              Last Name <span className="text-danger-500">*</span>
            </label>
            <input
              type="text"
              name="lastName"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full p-2 text-sm border border-neutral-300 rounded-md focus:border-primary-500"
            />
            {formik.touched.lastName && formik.errors.lastName && (
              <p className="mt-1 text-xs text-danger-500">{formik.errors.lastName}</p>
            )}
          </div>

          <div>
            <label className="block mb-1 text-sm font-medium text-neutral-700">
              Company <span className="text-danger-500">*</span>
            </label>
            <input
              type="text"
              name="company"
              value={formik.values.company}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="e.g., Spotify, Apple Music, Independent Curator"
              className="w-full p-2 text-sm border border-neutral-300 rounded-md focus:border-primary-500"
            />
            {formik.touched.company && formik.errors.company && (
              <p className="mt-1 text-xs text-danger-500">{formik.errors.company}</p>
            )}
          </div>

          <div>
            <label className="block mb-1 text-sm font-medium text-neutral-700">
              Role
            </label>
            <input
              type="text"
              name="role"
              value={formik.values.role}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="e.g., Playlist Curator, A&R, Editor"
              className="w-full p-2 text-sm border border-neutral-300 rounded-md focus:border-primary-500"
            />
            {formik.touched.role && formik.errors.role && (
              <p className="mt-1 text-xs text-danger-500">{formik.errors.role}</p>
            )}
          </div>

          <div>
            <label className="block mb-1 text-sm font-medium text-neutral-700">
              Email Address <span className="text-danger-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full p-2 text-sm border border-neutral-300 rounded-md focus:border-primary-500"
            />
            {formik.touched.email && formik.errors.email && (
              <p className="mt-1 text-xs text-danger-500">{formik.errors.email}</p>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 pt-4 border-t border-neutral-200">
            <Button
              variant="neutral"
              type="button"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? 'Saving...' : contact ? 'Update' : 'Add Contact'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddContactModal;
